import { Component, Prop, State, Watch, Element, h, Host, Event, EventEmitter, Listen } from '@stencil/core';

import {
  awaitTopbar,
  en,
  getMenuWidth,
  getTotalHeightOffset,
  isSmallScreen,
  reduceMotion,
  ripple,
  uuidv4,
} from '@/index';
import type {
  PnLanguages,
  PnActionMenuItem,
  Components,
  PnActionMenuInput,
  PnActionMenuGroup,
  PnMeasurements,
} from '@/index';

import { translations, PnMenuTextProp } from './translation';

import { angle_down, angle_left, angle_right, open_in_new } from 'pn-design-assets/pn-assets/icons.js';

/**
 * Create a list of actions in an accessible way.
 *
 * Option types include:
 *
 * - Regular button, click and it will collapse the menu,
 * - Checkbox/radio, toggle the option on/off
 * - Link, behaves like a regular `a[href]` element.
 *
 * You can group these actions in groups and/or sub menus.
 *
 * - `group`, an array of options. The label will act as a title for the items.
 * - `options`, an array of options. These items will appear in a sub-menu that can be toggled.
 *
 * @see {@link PnActionMenuItem}
 */
@Component({
  tag: 'pn-action-menu',
  styleUrl: 'pn-action-menu.scss',
})
export class PnActionMenu {
  private readonly id: string = `pn-action-menu-${uuidv4()}`;

  private menuButtonId: string = `${this.id}-button`;
  private menuListId: string = `${this.id}-list`;

  private menuTrigger: HTMLPnButtonElement;
  private menuContainer: HTMLDivElement;
  private menuList: HTMLMenuElement;

  private animation: Animation;
  private readonly duration: number = 400;
  private animationDuration: number = this.duration;

  private timeout: NodeJS.Timeout;

  /** 16em */
  private readonly menuWidth: number = 256;

  @Element() private hostElement: HTMLElement;

  @State() private smallMenu: boolean = null;
  @State() private upwards: boolean = false;

  @State() private activeSubmenu: PnActionMenuItem['value'][] = [];

  @State() private isClosing = false;
  @State() private isExpanding = false;

  /** Array of action menu options. @see {@link PnActionMenuItem} */
  @Prop() options: PnActionMenuItem[] = [];
  /** Set any prop from the `pn-button` component here. @see {@link Components.PnButton} */
  @Prop() button: Components.PnButton;

  /** Set a custom ID for the menu. */
  @Prop() menuId?: string = this.id;
  /** Manually set the language. */
  @Prop({ mutable: true }) language?: PnLanguages = null;

  /** Open/close the action menu manually. @category Features */
  @Prop({ mutable: true, reflect: true }) open?: boolean = false;
  /** Prefer that the menu open upwards, if there is enough space. @category Features */
  @Prop() menuUp?: boolean = false;
  /** Prefer that the submenus opens to the left, if there is enough space. @category Features */
  @Prop() menuLeft?: boolean = false;

  /** Emitted when the menu is opened or closed. */
  @Event() private menuToggle: EventEmitter<{ open: boolean }>;
  /** Emitted when the menu is fully hidden/visible after the animation has played. */
  @Event() private menuVisible: EventEmitter<{ visible: boolean }>;
  /** Emitted when an option is clicked (button, link, input or submenus). */
  @Event() private menuOption: EventEmitter<{
    /** Which type of menu item was clicked. */
    type: 'button' | 'link' | 'input' | 'submenu';
    /** If its an `input` type (checkbox/radio), it will be a generic `Event` (ChangeEvent) instead of `MouseEvent`. */
    click: MouseEvent | Event;
    /** The full {@link PnActionMenuItem} object. */
    option: PnActionMenuItem;
    /** If the submenu is open/closed. */
    open?: Boolean;
  }>;

  @Watch('options')
  handleOptions() {
    this.setMenuLayout();
  }

  @Watch('open')
  openHandler() {
    this.setAnimationDuration();

    requestAnimationFrame(() => {
      if (this.open && !(this.isClosing || this.isExpanding)) {
        this.setOffset();
        this.setMenuLayout();
      }

      this.gridHandler();

      this.menuToggle.emit({ open: this.open });

      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        if (!this.open) {
          this.activeSubmenu = [];
          const subMenus = this.hostElement.querySelectorAll<HTMLUListElement>('[data-x]');
          Array.from(subMenus).forEach(el => {
            el.removeAttribute('data-x');
            el.removeAttribute('data-y');
          });
        }

        this.menuVisible.emit({ visible: this.open });
      }, this.animationDuration);
    });
  }

  @Watch('menuId')
  handleMenuId() {
    this.menuButtonId = `${this.menuId}-button`;
    this.menuListId = `${this.menuId}-list`;
  }

  @Listen('resize', { target: 'window' })
  handleResize() {
    if (this.open) this.toggleActionMenu(false);
  }

  async componentWillLoad() {
    if (!this.options.length) console.warn(`${this.hostElement.localName}: No options set.`);

    this.handleMenuId();
    this.setAnimationDuration();

    if (this.language !== null) await awaitTopbar(this.hostElement);
  }

  componentDidLoad() {
    this.setOffset();

    if (this.open) this.gridHandler();
  }

  private translate(prop: PnMenuTextProp) {
    return translations[prop][this.language || en];
  }

  /** Open/close the action menu. */
  private toggleActionMenu(state?: boolean) {
    this.open = state ?? !this.open;
  }

  private setMenuLayout() {
    if (!this.menuContainer) return;

    if (!(this.isClosing || this.isExpanding)) this.resetMaxHeight();

    const measurements = this.getMenuMeasurements();

    this.setDirection(measurements);
    this.setMenuSize(measurements);
    this.setMaxHeight(measurements);
  }

  private setDirection({ hUp, hDown, sUp, sDown }: PnMeasurements) {
    const fitsUpwards = sUp > hUp;
    const fitsDownards = sDown > hDown;
    const moreSpaceDown = sDown > sUp;
    this.upwards = (this.menuUp && fitsUpwards) || (!fitsDownards && !moreSpaceDown);
  }

  private setMenuSize({ hUp, hDown, sUp, sDown }: PnMeasurements) {
    const isWidthSmall = isSmallScreen();
    const menuFits = this.upwards ? sUp > hUp : sDown > hDown;
    const isSmall = isWidthSmall || !menuFits;

    if (isSmall !== this.smallMenu) {
      this.smallMenu = isSmall;
    }
  }

  private setMaxHeight({ hUp, hDown, sUp, sDown }: PnMeasurements) {
    if (this.smallMenu) {
      const heightUp = hUp > sUp ? hUp - 16 : hUp;
      const heightDown = hDown > sDown ? hDown - 8 : hDown;
      this.hostElement.style.setProperty('--pn-menu-height', `${this.upwards ? heightUp : heightDown}px`);
    } else this.resetMaxHeight();
  }

  private resetMaxHeight() {
    this.hostElement.style.setProperty('--pn-menu-height', 'unset');
  }

  private setAnimationDuration() {
    if (reduceMotion()) this.animationDuration = 0;
    else this.animationDuration = this.duration;
  }

  private getRect(element: HTMLElement): DOMRect {
    return element.getBoundingClientRect();
  }

  private getMenuMeasurements(): PnMeasurements {
    const allLists = Array.from(this.menuContainer.querySelectorAll('menu'));
    const maxHeight = Math.max(...allLists.map(el => el.scrollHeight));

    const rectButton = this.getRect(this.menuTrigger);
    const rectMenu = this.getRect(this.menuContainer);

    /** Measurements upwards. */
    const sUp = rectButton.top - getTotalHeightOffset();
    const hUp = sUp > maxHeight ? maxHeight : sUp;

    /** Measurements downwards. */
    const sDown = innerHeight - rectMenu.top;
    const hDown = sDown > maxHeight ? maxHeight : sDown;

    return {
      hUp,
      hDown,
      sUp,
      sDown,
    };
  }

  private setOffset() {
    const data = this.getRect(this.hostElement);
    const fullWidth = data.left - getMenuWidth() + this.menuWidth;
    const isWide = fullWidth > innerWidth;
    const offset = isWide ? fullWidth - innerWidth + 16 : 0;

    if (this.menuContainer) this.menuContainer.style.setProperty('--pn-action-menu-offset', `-${offset}px`);
  }

  private getListId(option: PnActionMenuItem) {
    return `pn-menu-${option.value}-list`;
  }

  private getButtonId(option: PnActionMenuItem) {
    return `pn-menu-${option.value}-button`;
  }

  private getTriggerIcon() {
    if (this.button?.icon) return;
    return angle_down;
  }

  /** Set the path of open sub menus. */
  private getSubMenuPath(options: PnActionMenuItem[], value: string, path = []): string[] {
    for (const item of options) {
      const newPath = [...path, item.value];
      if (item.value === value) return newPath;

      if (item.options || item.group) {
        const result = this.getSubMenuPath(item.options || item.group, value, newPath);
        if (result) return result;
      }
    }
    return null;
  }

  private closeEachSubMenu() {
    this.menuTrigger?.querySelector('button')?.focus({ preventScroll: true });

    const interval = setInterval(() => {
      if (this.smallMenu || this.activeSubmenu.length === 0) {
        clearInterval(interval);
        this.toggleActionMenu(false);
      } else {
        this.activeSubmenu.pop();
        this.activeSubmenu = [...this.activeSubmenu];
      }
    }, 150);
  }

  private escButton(event: KeyboardEvent, option: PnActionMenuItem) {
    const { key } = event;

    if (key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();

      this.isSubmenuActive(option.value) ? this.toggleSub(option) : this.toggleActionMenu();
    }
  }

  // Animation Start

  private gridHandler() {
    if (this.open) this.openGrid();
    else this.closeGrid();
  }

  private openGrid() {
    const list = this.getRect(this.menuList);

    const { clientHeight } = this.menuContainer;
    const height = this.isClosing ? clientHeight : 0;
    this.menuContainer.style.height = `${list.height}px`;

    this.isExpanding = true;
    this.animateGrid(true, `${height}px`, `${list.height}px`);
  }

  private closeGrid() {
    const list = this.getRect(this.menuList);

    const { clientHeight } = this.menuContainer;
    const height = this.isExpanding ? clientHeight : list.height;
    this.menuContainer.style.height = `0px`;

    this.isClosing = true;
    this.animateGrid(false, `${height}px`, `0px`);
  }

  private animateGrid(open: boolean, startHeight: string, endHeight: string) {
    this.cancelAnimations();
    this.animation = this.menuContainer.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: this.animationDuration,
        easing: 'cubic-bezier(0.6, 0, 0.2, 1)',
      },
    );
    this.animation.onfinish = () => this.animationFinish();
    this.animation.oncancel = () => (open ? (this.isExpanding = false) : (this.isClosing = false));
  }

  private animationFinish() {
    this.cancelAnimations();
    this.menuContainer.style.height = this.isClosing ? '0px' : '';
    this.isClosing = false;
    this.isExpanding = false;
  }

  private cancelAnimations() {
    if (this.animation) this.animation.cancel();
  }

  // Animation end

  private async optionSelect(option: PnActionMenuItem, click: MouseEvent | Event) {
    const type = option?.options?.length ? 'submenu' : !!option.input ? 'input' : option.href ? 'link' : 'button';
    const isSubMenu = type === 'submenu';

    if (isSubMenu) this.toggleSub(option);
    else if (type === 'button') this.closeEachSubMenu();
    else if (type === 'input') option.checked = (click.target as HTMLInputElement).checked;

    this.menuOption.emit({
      option,
      type,
      click,
      open: isSubMenu ? this.isSubmenuActive(option.value) : null,
    });

    const target = click.target as HTMLElement;
    const element =
      target.localName === 'input'
        ? (target.nextElementSibling as HTMLElement)
        : target.className === 'pn-action-menu-button'
          ? target
          : target.closest<HTMLElement>('.pn-action-menu-button');

    const { x, width, y, top } = this.getRect(element as HTMLElement);
    const clientCor = { clientX: x + width - 24, clientY: y - top };

    ripple(click.type === 'click' ? (click as MouseEvent) : clientCor, element);

    this.menuContainer.scrollTo({ top: 0 });
  }

  /** Toggle individual sub-menus inside the action menu by using the `option['value']`. */
  private toggleSub(option: PnActionMenuItem) {
    const { value } = option;

    const path = this.getSubMenuPath(this.options, value);
    const isActive = this.activeSubmenu.includes(value);
    isActive && path.splice(this.activeSubmenu.indexOf(value), 1);

    const item = this.hostElement.querySelector<HTMLElement>(`#${this.getListId(option)}`);
    const data = this.getRect(item);

    const spaceLeft = data.left;
    const spaceRight = innerWidth - data.right - 8;

    const fitsLeft = spaceLeft > data.width;
    const fitsRight = spaceRight > data.width;

    const climbUp = this.menuUp && data.top - data.height > 0;
    const yDirection = climbUp ? 'top' : 'bottom';

    if (!isActive && !item.dataset.x)
      item.setAttribute(
        'data-x',
        this.menuLeft && fitsLeft ? 'left' : fitsRight ? 'right' : fitsLeft ? 'left' : 'center',
      );
    if (!isActive && !item.dataset.y) item.setAttribute('data-y', yDirection);

    if (JSON.stringify(path) === JSON.stringify(this.activeSubmenu))
      this.activeSubmenu = this.activeSubmenu.filter(item => item !== value);
    else this.activeSubmenu = [...path];
  }

  /** Check if a sub-menu is active. */
  private isSubmenuActive(value: string): boolean {
    return this.activeSubmenu.includes(value);
  }

  private isMenuActive() {
    const isRootSubInsideGroup = this.options.find(({ value }) => this.activeSubmenu.every(val => val === value));
    return this.smallMenu && (this.activeSubmenu?.length === 0 || isRootSubInsideGroup);
  }

  private isCurrentSubMenu(value: string) {
    return Boolean(this.activeSubmenu[this.activeSubmenu.length - 1] === value);
  }

  private getOptionTrailing(option: PnActionMenuItem) {
    const useButtonIcon = option.options?.length && angle_right;
    const useTrailingIcon = option.trailingIcon;
    const useLinkIcon = option.target === '_blank' && open_in_new;

    /** If the user has defined a trialing icon, use it first. */
    const icon = useButtonIcon || useTrailingIcon || useLinkIcon;

    if (icon) {
      return <pn-icon icon={icon} color="blue700" data-suffix data-active={this.isSubmenuActive(option.value)} />;
    }
    if (option.suffix) {
      return <span class="pn-action-menu-item-suffix">{option.suffix}</span>;
    }
  }

  private renderCheckbox(option: PnActionMenuInput) {
    const id = `pn-menu-${option.value}-label`;
    const idHelper = `pn-menu-${option.value}-helpertext`;
    return (
      <div class="pn-action-menu-item-content">
        <input
          type={option.input}
          id={id}
          class="pn-action-menu-input"
          name={option.name}
          value={option.value}
          checked={option.checked}
          disabled={option.disabled}
          aria-describedby={option.helpertext ? idHelper : null}
          onInput={event => this.optionSelect(option, event)}
          tabIndex={this.open ? null : -1}
        />

        <div class="pn-action-menu-button">
          {!!option.icon && <pn-icon icon={option.icon} color="blue700" />}

          <div class="pn-action-menu-item-text">
            <label htmlFor={id} class="pn-action-menu-item-label">
              {option.label}
            </label>
            {option.helpertext && (
              <p id={idHelper} class="pn-action-menu-item-helpertext">
                <span>{option.helpertext}</span>
              </p>
            )}
          </div>
          {option.input === 'checkbox' ? (
            <div class="pn-action-menu-checkbox">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                <polyline class="pn-action-menu-checkbox-checkmark-path" points="4,12 9,17 20,6" stroke-width="3" />
              </svg>
            </div>
          ) : (
            <div class="pn-action-menu-radio">
              <div class="pn-action-menu-radio-outer">
                <div class="pn-action-menu-radio-inner" />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  private renderButton(option: PnActionMenuItem) {
    const isCloseButton = option.label === 'BACK';
    const isSub = !!option?.options?.length;
    const isGroup = !!option?.group?.length;
    const isLink = !!option.href && !isSub && !isGroup;

    const appendedId = isCloseButton ? '-close' : '';
    const hasIcon = isCloseButton || !!option.icon;

    const trailingObject = isCloseButton ? null : this.getOptionTrailing(option);
    const open = this.isSubmenuActive(option.value);
    const subMenuAttrs = isSub
      ? {
          'aria-haspopup': 'true',
          'aria-controls': open ? this.getListId(option) : null,
          'aria-expanded': open?.toString(),
        }
      : {};

    const attrs = isLink
      ? {
          href: option.href,
          target: option.target,
        }
      : {
          disabled: option.disabled,
        };

    const Tag = isLink ? 'a' : 'button';

    return isGroup ? (
      <div class="pn-action-menu-group-label">
        <p class="pn-action-menu-p">{option.label}</p>
        {option.helpertext && <p class="pn-action-menu-group-helpertext">{option.helpertext}</p>}
      </div>
    ) : (
      <div class="pn-action-menu-item-content" data-close={isCloseButton}>
        <Tag
          id={this.getButtonId(option) + appendedId}
          class="pn-action-menu-button"
          {...subMenuAttrs}
          {...attrs}
          tabIndex={this.open ? null : -1}
          onClick={event => this.optionSelect(option, event)}
          onKeyDown={(event: KeyboardEvent) => this.escButton(event, option)}
        >
          {hasIcon && <pn-icon icon={isCloseButton ? angle_left : option.icon} color="blue700" />}
          <div class="pn-action-menu-item-text">
            <span class="pn-action-menu-item-label">{isCloseButton ? this.translate('BACK') : option.label}</span>
            {option.helpertext && !isLink && <span class="pn-action-menu-item-helpertext">{option.helpertext}</span>}
          </div>
          {trailingObject}
        </Tag>
      </div>
    );
  }

  private renderSub(option: PnActionMenuItem) {
    return (
      <menu
        id={this.getListId(option)}
        aria-labelledby={this.getButtonId(option)}
        class="pn-action-menu-sub"
        data-open={this.isSubmenuActive(option.value)?.toString()}
        data-current={this.isCurrentSubMenu(option.value)}
      >
        {this.smallMenu && (
          <li class="pn-action-menu-item">
            {this.renderButton({
              label: 'BACK',
              value: option.value,
              options: option.options,
            })}
          </li>
        )}
        {option.options.map(item => this.renderMenuItem(item))}
      </menu>
    );
  }

  private renderGroup(option: PnActionMenuGroup) {
    return <menu class="pn-action-menu-group">{option.group.map(item => this.renderMenuItem(item))}</menu>;
  }

  private renderMenuItem(option: PnActionMenuItem) {
    const isSub = !!option?.options?.length;
    const isGroup = !isSub && !!option?.group?.length;

    const isCheckbox = !!option.input && !isSub && !isGroup;

    return (
      <li class="pn-action-menu-item" data-group={isGroup} data-sub={isSub}>
        {isCheckbox ? this.renderCheckbox(option) : this.renderButton(option)}
        {isSub ? this.renderSub(option) : isGroup && this.renderGroup(option)}
      </li>
    );
  }

  private renderMenu() {
    return (
      <div
        id={this.menuListId}
        class="pn-action-menu-container"
        role="region"
        aria-labelledby={this.menuButtonId}
        data-open={this.open}
        data-moving={this.isClosing || this.isExpanding}
        data-upwards={this.upwards}
        data-small={this.smallMenu}
        style={{ height: '0px' }}
        ref={el => (this.menuContainer = el)}
      >
        <menu class="pn-action-menu-list" ref={el => (this.menuList = el)} data-current={this.isMenuActive()}>
          {this.options?.map(option => this.renderMenuItem(option))}
        </menu>
      </div>
    );
  }

  render() {
    return (
      <Host>
        <div id={this.menuId !== this.id ? this.menuId : null} class="pn-action-menu">
          <pn-button
            icon={this.getTriggerIcon()}
            {...this.button}
            buttonId={this.menuButtonId}
            tooltipUp={!this.upwards}
            ariahaspopup="true"
            ariacontrols={this.open ? this.menuListId : null}
            ariaexpanded={this.open.toString()}
            data-default-icon={!this.button?.icon}
            onPnClick={() => this.toggleActionMenu()}
            ref={el => (this.menuTrigger = el)}
          />
          {this.renderMenu()}
        </div>
      </Host>
    );
  }
}
